//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';

// === COMPONENTS ===
import { FileInfo   } from './components/FileInfo/FileInfo'     ;
import { Packages   } from './components/Packages/Packages'     ;
import { Exceptions } from './components/Exceptions/Exceptions' ;
import { DocumentView } from './components/DocumentView/DocumentView';
import { MissingDocs } from './components/MissingDocs/MissingDocs';

// === API ===
import { on, removeListener, requestFile, requestFileEvaluation, requestFileResults } from 'api/prokey/prokey.websocket';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

//-----------------------------------------------------------------
export function FileView() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { file_id } = useParams();

  const navigate = useNavigate();

  const [ file       , setFile      ] = useState( null  );
  const [ is_loading , setIsLoading ] = useState( false );
  const [ selectedPackages , setSelectedPackages ] = useState( [  ] );  

  //#endregion

  //#region [STYLES]

  const theme = useTheme();
  const _styles = {
    container: {
      height: '100%',
      boxShadow: 0,
      marginTop: '1rem',
      marginLeft: '1rem',
      marginRight: '1rem',
    },
    card: {
      width        : '100%',
      maxWidth     : '100%',
      overflowY    : 'hidden',
      marginBottom : '1rem',
    },
    controls: {
      display      : 'flex',
      borderTop    : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem 1rem 0 0',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    title: {
      display      : 'flex',
      flexGrow     : 1,
      borderTop    : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    container_content: {
      display : 'block',
      height  : '100%' ,
    },
    container_file_info: {
      width        :'100%'  ,
      height       :'5rem'  ,
      display      :'block' ,
      marginBottom :'1rem'  ,
    },
    container_rule_info: {
      width        :'100%' ,
      height       :'calc( 100% - 6rem )'  ,
      display      :'flex' ,
      backgroundColor : theme.palette.panel.main,
    },
    container_documents: {
      width   :'calc( 40% )' ,
      height  :'calc( 100% - 7rem )' ,
      display :'block'       ,
    },
    task_queue_table: {
      width          : '100%',
      'table-layout' : 'fixed',
    },
    table_header_cell: {
      backgroundColor : theme.palette.primary.main + getHexAlpha(100),
      color           : theme.palette.primary.contrastText,
    },
    task_queue_text: {
      margin   : '1rem',
      fontSize : 'x-large',
    },
    right: {
      marginLeft: 'auto'
    },
    align_right: {
      float: "right",
    },
    height: {
      height: '100%',
    },
    button_export: {
      height: '4rem',
      margin: '0 0.5rem',
    },
    button_evaluate: {
      height: '4rem',
      margin: '0 0.5rem',
    },
    button_close: {
      height: '4rem',
      margin: '0 0.5rem',
    },
    spinner_box: {
      position  : 'fixed' ,
      top       : '50%'   ,
      left      : '50%'   ,
      height    : '14rem' ,
      width     : '10rem' ,
      textAlign : 'center'
    },
  }

  const
    CONTAINER = {
      sx: _styles.container,
    },
    CONTAINER_CONTROLS = {
      sx: _styles.controls,
    },
    TITLE = {
      sx: _styles.title,
    },
    CONTAINER_CONTENT = {
      sx: _styles.container_content,
    },
    CONTAINER_FILE_INFO = {
      sx: _styles.container_file_info,
    },
    CONTAINER_RULE_INFO = {
      sx: _styles.container_rule_info,
    },
    CONTAINER_DOCUMENTS = {
      sx: _styles.container_documents,
    },
    BUTTON_EXPORT = {      
      sx       : _styles.button_export,
      variant  : 'contained',
      color    : 'primary',
      onClick  : () => requestFileResults(file.file_id),
      children : [ 'Export' ],
    },
    BUTTON_EVALUATE = {      
      sx       : _styles.button_evaluate,
      disabled : file?.field_count > 10000,
      variant  : 'contained',
      color    : 'primary',
      onClick  : onClick_Evaluate,
      children : [ 'Evaluate' ],
    },
    BUTTON_CLOSE = {      
      sx       : _styles.button_close,
      variant  : 'contained',
      color    : 'error',
      onClick  : () => { navigate('analytics') },
      children : [ 'X' ],
    }
    
  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------    
    function listener_file(data) {
      //-----------------------------------------------------------------
      setFile(data);
      setIsLoading( false );
    }
    //-----------------------------------------------------------------    
    function listener_status(data) {
      //-----------------------------------------------------------------
      requestFile( file_id );
    }

    on( 'FILE' , listener_file );
    on( 'FILE_EVALUATE_STATUS' , listener_status );
    
    requestFile( file_id );

    return () => {
      removeListener( 'FILE' , listener_file );
      removeListener( 'FILE-RESULTS' , downloadResults );
    };
  }, [] ); // empty dependency = called only once at startup

  useEffect( () => {
    on( 'FILE-RESULTS', downloadResults )
    
    return () => {
      removeListener( 'FILE-RESULTS' , downloadResults );
    };
  }, [file] );

  //#endregion

  //#region [MODIFY]

  //-----------------------------------------------------------------
  function highlightPackage(rule_id) {
    //-----------------------------------------------------------------
    try {
      const rule = file.rules.find( x => x.id == rule_id );
      if( !rule || !rule.exceptions) return;

      const packages = rule.exceptions.map( result => result.package_id );
      setSelectedPackages(packages);
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function downloadResults( data ) {
    //-----------------------------------------------------------------
    if( !data ) {
      return;
    }

    const date_curr = dayjs().format( 'YYYYMMDD' );
    const file_name = `file-analysis-${file.loan_number}-${date_curr}`;

    const json_string    = JSON.stringify( data );
    const json_blob      = new Blob( [json_string], { type: 'application/json' } );
    const json_url       = window.URL.createObjectURL( json_blob );
    const json_hyperlink = document.createElement('a');

    json_hyperlink.setAttribute('href', json_url);
    json_hyperlink.setAttribute('download', file_name);
    json_hyperlink.click();
  };

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onRuleHover(event) {
    //-----------------------------------------------------------------
    try {
      highlightPackage(event.currentTarget.id);
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick_Evaluate(event) {
    //-----------------------------------------------------------------
    try {
      requestFileEvaluation(file.file_id)
      setIsLoading( true );
    } catch (error) {
      console.warn(error);
    }
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( !file ) return null;
    
    if(is_loading) return (
      <Box sx={_styles.container}>
        <Paper sx={_styles.spinner_box}>
          <CircularProgress sx={_styles.spinner} size='6rem' />
          <Typography variant='button' sx={_styles.spinner_caption} display='block'>
            Evaluating
          </Typography>
        </Paper>
      </Box>
    );

    return (
      <Box {...CONTAINER}>
        <Box {...CONTAINER_CONTROLS}>
          <Box {...TITLE}>
            <Typography variant='h4' sx={_styles.task_queue_text}>
              {file.loan_number}
            </Typography>
          </Box>
          <Button { ...BUTTON_EXPORT   } />
          <Button { ...BUTTON_EVALUATE } />
          <Button { ...BUTTON_CLOSE    } />
        </Box>
        <Box {...CONTAINER_CONTENT}>
          <Box {...CONTAINER_FILE_INFO}>
            <FileInfo file={file} />
          </Box>
          <Box {...CONTAINER_RULE_INFO}>
            <MissingDocs file={file} />
            <DocumentView file={file} />
            <Exceptions file_id={file.file_id} items={file.rules} onHover={onRuleHover} type={'DATA'} />
          </Box>
        </Box>
      </Box>
    );
  }

  //#endregion

  return display();
}
